import { GridColFullWidth } from '@constants/layout';
import { PlanCardType, PricingMainType } from '@lib/sanity/queries/modules/pricingMain/types';
import { RgbaColor } from '@lib/sanity/sanity.types';
import Text, { TextVariant } from '@ui/Text/Text';
import PortableText from '@ui/Text/PortableText';
import cn from '@utils/cn';
import { usePricingFrequency } from '../PricingMain/PricingFrequencyContext';
import MostPopularTag from './MostPopularTag';
import PlanCTAButton from './PlanCTAButton';
import PlanFeatureVariant from './PlanFeatureVariant';
import PlanPrice from './PlanPrice';

type Props = {
  data: PricingMainType['cardsPricing'];
};

const PlanCardsDesktop = ({ data }: Props) => {
  const { isYearlyFrequency } = usePricingFrequency();
  const mostPopularIndex = data?.[0]?.mostPopularIndex;
  const mostPopularTextColor: RgbaColor | undefined = data?.[0]?.mostPopularChipColorText?.rgb;
  const mostPopularChipColor: RgbaColor | undefined = data?.[0]?.mostPopularChipColor?.rgb;

  const hasActiveDiscount = (planCard: PlanCardType) => {
    if (planCard.discount) {
      const { discountLabel, discountFrequency } = planCard.discount;
      const shouldApplyDiscount =
        discountFrequency === 'both' ||
        (isYearlyFrequency && discountFrequency === 'yearly') ||
        (!isYearlyFrequency && discountFrequency === 'monthly');

      return shouldApplyDiscount && discountLabel;
    }
    return false;
  };

  const getTagLabel = (planCard: PlanCardType) => {
    if (hasActiveDiscount(planCard) && planCard.discount?.discountLabel) {
      return planCard.discount.discountLabel;
    }
    return data?.[0]?.mostPopularLabel;
  };

  const shouldShowTag = (planCard: PlanCardType, index: number) => {
    return hasActiveDiscount(planCard) || mostPopularIndex === index;
  };

  return (
    <div className={cn('hidden lg:block', GridColFullWidth)}>
      <div className="flex w-full justify-end pb-2">
        <PortableText
          className="text-sm text-gray-500"
          variant={TextVariant.TextDescription}
          content={data?.[0]?.allPricesAreInCurrency}
        />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-4">
        <Text
          variant={TextVariant.SubtitleSM}
          content="FOR INDIVIDUALS"
          className="text-center uppercase tracking-widest text-gray-500 lg:col-span-2 xl:col-start-1 xl:row-start-1"
        />
        <Text
          variant={TextVariant.SubtitleSM}
          content="FOR BUSINESSES"
          className="text-center uppercase tracking-widest text-gray-500 lg:col-span-2 lg:order-3 xl:order-0 xl:col-span-2 xl:col-start-3 xl:row-start-1"
        />
        {data?.[0]?.planCard?.map((planCard, index) => {
          const isBusinessPlan = index >= 2;
          return (
            <div
              className={cn('relative flex flex-col xl:row-start-2', isBusinessPlan ? 'lg:order-4' : 'lg:order-2')}
              key={`plan-${index}`}
            >
              <div
                className={cn('flex h-full w-full flex-col rounded-2xl p-6 transition-shadow')}
                style={
                  planCard.planCardColor?.rgb && planCard.planCardTopBorderColor?.rgb
                    ? {
                        backgroundColor: `rgba(${planCard.planCardColor.rgb.r},${planCard.planCardColor.rgb.g},${planCard.planCardColor.rgb.b},${planCard.planCardColor.rgb.a})`,
                        borderColor: `rgb(${planCard.planCardTopBorderColor.rgb.r},${planCard.planCardTopBorderColor.rgb.g},${planCard.planCardTopBorderColor.rgb.b},${planCard.planCardTopBorderColor.rgb.a})`,
                        borderWidth: '1px',
                      }
                    : undefined
                }
              >
                <div className="flex h-full w-full flex-col items-start gap-6">
                  <div className="flex w-full flex-col gap-3">
                    <div className="flex w-full items-start justify-between gap-2">
                      <PortableText
                        content={planCard.title}
                        variant={TextVariant.TitleXS}
                        className="font-bold text-gray-900"
                      />
                      {shouldShowTag(planCard, index) && (
                        <MostPopularTag
                          mostPopularChipColor={
                            hasActiveDiscount(planCard) && planCard.discount?.discountLabelColor?.rgb
                              ? planCard.discount.discountLabelColor.rgb
                              : mostPopularChipColor
                          }
                          mostPopularTextColor={mostPopularTextColor}
                          content={getTagLabel(planCard)}
                        />
                      )}
                    </div>
                    <PortableText
                      content={planCard.description}
                      variant={TextVariant.TextBodyMD}
                      className="text-gray-500 min-h-[50px]"
                    />
                  </div>
                  <PlanPrice planCard={planCard} />
                  <div className="h-px w-full bg-gray-200" />
                  <div className="flex w-full flex-col gap-3">
                    <PortableText
                      content={planCard.featuresTitle}
                      variant={TextVariant.TextBodyMD}
                      className="font-semibold text-gray-700"
                    />
                    {planCard.planFeatures?.map((feature, featIndex) => (
                      <PlanFeatureVariant data={feature} index={featIndex} key={featIndex} />
                    ))}
                  </div>
                  <div className="mt-auto w-full">
                    <PlanCTAButton planCard={planCard} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlanCardsDesktop;
